import React, { useCallback, useMemo } from "react"
import { get, size } from "lodash"
import { Box, useTheme } from "@mui/material"
import { useNavigate, useParams } from "react-router-dom"

import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"
import { useOccupantPromotion } from "hooks/promotion/useOccupantPromotion"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { useGeoLocation } from "providers/geolocation"

import { PageCard, PageCardContent } from "components/card"
import { PercentIcon } from "components/icons"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { Carousel, CarouselItem } from "components/Carousel"
import { EmptyData } from "components/EmptyData"
import { ContentWithAside } from "layouts/kiosk-h"
import { PromotionCard } from "extensions/iconsiam/components/domains/promotions"
import { PlaceDetailSideBar } from "extensions/iconsiam/components/domains/features/PlaceDetailSideBar"

export const OccupantPromotionKioskHPage = ({
  Layout = ContentWithAside,
  AppNavigation = DefaultAppNavigation,
}) => {
  const navigate = useNavigate()
  const { occupantId } = useParams()
  const { t } = useTranslation()
  const { promotions, ...data } = useOccupantPromotion(occupantId)
  const { deviceLocation } = useGeoLocation()

  const isReadyToNavigate = !!deviceLocation && get(data, "isNavigatable", true)
  const hasPromotions = size(promotions) > 0

  const theme = useTheme()
  const slidePerPage = 3
  const carouselType =
    promotions && promotions.length < slidePerPage ? "slide" : "loop"
  const isAutoplay = promotions && promotions.length > slidePerPage

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.promotions) || <PercentIcon />,
    [theme]
  )

  const { pageCardSideBar } =
    theme.components[`PlaceDetailPage`]?.styleOverrides || {}

  const { styleOverrides } = theme.components?.[`OccupantPromotionPage`] || {}
  const { promotionCarouselOption } =
    theme.components[`OccupantPromotionPage`]?.defaultProps || {}
  const {
    pageCard,
    pageCardContent,
    carouselContainer,
    carousel,
    emptyDataContainer,
  } = styleOverrides

  const handleClickSlide = useCallback(
    (_splide, { slide }, _event) => {
      navigate(slide.getAttribute("link"))
    },
    [navigate]
  )

  const onNavigate = useCallback(() => {
    if (!isReadyToNavigate) return
    navigate(`/maps/dir/unknown/${data?.id}`)
  }, [navigate, isReadyToNavigate, data?.id])

  usePageSideBarLayout({
    sidebar: {
      open: true,
    },
  })

  const direction = theme.direction

  return (
    <Layout
      aside={
        <PageCard sx={pageCardSideBar}>
          <PlaceDetailSideBar
            data={data}
            onNavigate={onNavigate}
            showAdditionalButtons={false}
          />
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent sx={pageCardContent}>
          <Box sx={carouselContainer}>
            {!hasPromotions ? (
              <Box sx={emptyDataContainer}>
                <EmptyData
                  icon={emptyIcon}
                  primary={t("No Current Promotions")}
                  secondary={t("Check Back Soon")}
                />
              </Box>
            ) : (
              <Carousel
                onClick={handleClickSlide}
                options={{
                  type: carouselType,
                  perPage: slidePerPage,
                  pagination: true,
                  trimSpace: false,
                  autoplay: isAutoplay,
                  gap: "24px",
                  focus: "center",
                  direction: direction,
                  ...promotionCarouselOption,
                }}
                sx={carousel}
              >
                {promotions.map((promotion) => {
                  const sponsoredId = promotion?.properties?.sponsoredId || ""
                  return (
                    <CarouselItem
                      link={`/occupant-promotions/${promotion.id}`}
                      key={`${sponsoredId}${promotion.id}`}
                    >
                      <PromotionCard promotion={promotion} />
                    </CarouselItem>
                  )
                })}
              </Carousel>
            )}
          </Box>
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
